import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Check, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SEARCH_FABRIC_GROUPS_FOR_PALETTE } from '../fabricPaletteQueries'

interface FabricGroupPickerProps {
  /** ids already in the palette (shown checked and not selectable) */
  selectedIds: number[]
  disabled?: boolean
  onPick: (groupId: number) => void
}

export function FabricGroupPicker({
  selectedIds,
  disabled,
  onPick,
}: FabricGroupPickerProps) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setTerm(input), 250)
    return () => clearTimeout(t)
  }, [input])

  const { data, loading } = useQuery(SEARCH_FABRIC_GROUPS_FOR_PALETTE, {
    variables: { searchTerm: term, take: 50 },
    skip: !open,
    fetchPolicy: 'network-only',
  })
  const groups: any[] = data?.findManyFabricGroup ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          <Plus className="mr-1 h-4 w-4" />
          Add fabric group
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        {/* filtering is server-side, so turn off cmdk's own filter */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by code or name..."
            value={input}
            onValueChange={setInput}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Searching...' : 'No fabric groups found.'}
            </CommandEmpty>
            {groups.map(g => {
              const added = selectedIds.includes(g.id)
              const name = g.fabric_group_profiles?.[0]?.name
              return (
                <CommandItem
                  key={g.id}
                  value={String(g.id)}
                  disabled={added}
                  onSelect={() => {
                    if (added) return
                    onPick(g.id)
                    setOpen(false)
                    setInput('')
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${added ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="font-mono text-sm mr-2">{g.code}</span>
                  <span className="truncate">{name}</span>
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
